import { useRef, useState } from 'react';
import { Upload, ArrowRight, Check, AlertTriangle, SkipForward } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import {
  Badge, Button, Card, CardHeader, Field, LoadingBlock, Select, Table, TableWrap,
  Td, Th, useToast,
} from './ui';

interface ImportField {
  key: string;
  label: string;
  required: boolean;
  type: string;
}

interface ImportOptions {
  datasets: { key: string; label: string; fields: ImportField[] }[];
  max_rows: number;
}

interface Preview {
  dataset: string;
  headers: string[];
  suggested: Record<string, number>;
  sample: string[][];
  row_count: number;
  fields: ImportField[];
}

interface RunResult {
  dry_run: boolean;
  created: number;
  skipped: number;
  failed: number;
  results: { line: number; status: string; detail: string }[];
}

/**
 * Bringing records in from whatever they are using now.
 *
 * Three steps, and the middle one is the point: PROVIA guesses which of their
 * columns means which of ours, and then shows every guess as a dropdown that
 * can be changed. A silent guess that puts a deposit into a rent column
 * produces a database that is confidently wrong — worse than an afternoon of
 * typing, because nobody finds out for months.
 *
 * Nothing is written until they have seen a row-by-row report of what would
 * happen. It is the only screen in the app where one click can create hundreds
 * of records.
 */
export function DataImport() {
  const toast = useToast();
  const { data: options } = useApi<ImportOptions>('/import');
  const fileRef = useRef<HTMLInputElement>(null);

  const [dataset, setDataset] = useState('tenants');
  const [csv, setCsv] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<Record<string, number | ''>>({});
  const [result, setResult] = useState<RunResult | null>(null);
  const [busy, setBusy] = useState(false);

  function reset() {
    setCsv(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function read(file: File) {
    setBusy(true);
    try {
      const text = await file.text();
      const res = await api.post<Preview>('/import/preview', { dataset, csv: text });
      setCsv(text);
      setPreview(res);
      setMapping(res.suggested as Record<string, number>);
      setResult(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That file could not be read');
      reset();
    } finally {
      setBusy(false);
    }
  }

  async function run(dryRun: boolean) {
    if (!csv) return;
    setBusy(true);
    try {
      const res = await api.post<RunResult>('/import/run', {
        dataset, csv, mapping, dry_run: dryRun,
      });
      setResult(res);
      if (!dryRun) {
        toast.success(`${res.created} added, ${res.skipped} already here, ${res.failed} could not be read`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That import could not be run');
    } finally {
      setBusy(false);
    }
  }

  if (!options) return <LoadingBlock rows={3} />;

  const tone = (status: string) => (
    status === 'failed' ? 'danger' : status === 'skipped' ? 'warn' : 'ok'
  );

  return (
    <Card>
      <CardHeader
        title="Bring your records in"
        subtitle="From a spreadsheet, or from whatever you are using now"
      />
      <div className="p-5 sm:p-6 pt-4 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="What you are importing">
            <Select
              value={dataset}
              onChange={(e) => { setDataset(e.target.value); reset(); }}
            >
              {options.datasets.map((d) => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Your file" hint={`A spreadsheet exported as CSV, up to ${options.max_rows} rows`}>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); }}
            />
            <Button
              icon={<Upload className="h-4 w-4" />}
              loading={busy && !preview}
              onClick={() => fileRef.current?.click()}
              className="w-full"
            >
              {csv ? 'Choose a different file' : 'Choose a file'}
            </Button>
          </Field>
        </div>

        {/*
          Import order matters and nothing else says so. Tenancies point at
          properties and tenants, so a tenancy file imported first fails every
          row and looks like a broken feature.
        */}
        <p className="text-xs text-faint leading-relaxed">
          Import in this order: properties, then rooms, then tenants, then tenancies — each one
          points at the ones before it. Running the same file twice is safe; anything already
          here is skipped rather than added again.
        </p>

        {preview && (
          <>
            <div>
              <h4 className="font-semibold text-sm mb-1">Match your columns</h4>
              <p className="text-sm text-muted mb-3">
                {preview.row_count} rows. We have had a guess — change anything that is wrong,
                and leave out anything you do not want.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {preview.fields.map((field) => (
                  <Field
                    key={field.key}
                    label={field.required ? `${field.label} *` : field.label}
                  >
                    <Select
                      value={mapping[field.key] ?? ''}
                      onChange={(e) => setMapping((m) => ({
                        ...m,
                        [field.key]: e.target.value === '' ? '' : Number(e.target.value),
                      }))}
                    >
                      <option value="">— not in my file —</option>
                      {preview.headers.map((h, i) => (
                        <option key={`${h}-${i}`} value={i}>{h || `Column ${i + 1}`}</option>
                      ))}
                    </Select>
                  </Field>
                ))}
              </div>
            </div>

            {/* Their own rows, under our headings, before they agree to anything. */}
            <TableWrap>
              <Table>
                <thead>
                  <tr>{preview.headers.map((h, i) => <Th key={i}>{h}</Th>)}</tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, i) => (
                    <tr key={i}>
                      {preview.headers.map((_, c) => (
                        <Td key={c} className="text-muted whitespace-nowrap">{row[c]}</Td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant={result && !result.dry_run ? 'secondary' : 'primary'}
                loading={busy}
                icon={<ArrowRight className="h-4 w-4" />}
                onClick={() => void run(true)}
              >
                Check it first
              </Button>
              {result?.dry_run && result.created > 0 && (
                <Button variant="primary" loading={busy} onClick={() => void run(false)}>
                  Import {result.created} {result.created === 1 ? 'record' : 'records'}
                </Button>
              )}
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <Badge tone="ok">
                <Check className="h-3 w-3" />
                {result.dry_run ? `${result.created} ready` : `${result.created} added`}
              </Badge>
              {result.skipped > 0 && (
                <Badge tone="warn"><SkipForward className="h-3 w-3" /> {result.skipped} already here</Badge>
              )}
              {result.failed > 0 && (
                <Badge tone="danger">
                  <AlertTriangle className="h-3 w-3" /> {result.failed} could not be read
                </Badge>
              )}
            </div>

            {result.results.filter((row) => row.status !== 'ready' && row.status !== 'created').length > 0 && (
              <TableWrap>
                <Table>
                  <thead>
                    <tr><Th>Line</Th><Th>What happened</Th><Th>Why</Th></tr>
                  </thead>
                  <tbody>
                    {result.results
                      .filter((row) => row.status !== 'ready' && row.status !== 'created')
                      .map((row) => (
                        <tr key={row.line}>
                          <Td>{row.line}</Td>
                          <Td><Badge tone={tone(row.status)}>{row.status}</Badge></Td>
                          <Td className="text-muted">{row.detail}</Td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
